// Certificate design model shared by the Graphic Studio canvas + PDF renderer.
// All geometry is in millimetres on an A4 portrait page (210 × 297).

export const PAGE = { w: 210, h: 297 };

export const FONTS = [
  { value: "helvetica", label: "Sans (Helvetica)", css: "Helvetica, Arial, sans-serif" },
  { value: "times", label: "Serif (Times)", css: "'Times New Roman', Times, serif" },
  { value: "courier", label: "Mono (Courier)", css: "'Courier New', Courier, monospace" },
];
export const fontCss = (f) => (FONTS.find((x) => x.value === f) || FONTS[0]).css;

export const PLACEHOLDERS = [
  { key: "member", label: "Member name" },
  { key: "image_title", label: "Image title" },
  { key: "competition", label: "Competition" },
  { key: "place", label: "Place (1st, 2nd…)" },
  { key: "score", label: "Score / total" },
  { key: "judge", label: "Judge" },
  { key: "season", label: "Season" },
  { key: "club", label: "Club name" },
  { key: "code", label: "Photographer code" },
  { key: "rank", label: "Rank number" },
  { key: "date", label: "Today's date" },
];

export const SAMPLE_ROW = {
  id: "sample", code: "AB", name: "Member Name", competition: "Competition Name",
  image_title: "Image Title", score: "10", total: "10", rank: "1", place: "1st",
  judge: "Judge Name", season: "Season", club: "Camera Club",
};

const todayStr = () =>
  new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

export function substitute(text, row) {
  if (!text) return "";
  const r = row || {};
  const map = {
    member: r.name, name: r.name, image_title: r.image_title, competition: r.competition,
    place: r.place, score: r.score, total: r.total ?? r.score, judge: r.judge,
    season: r.season, club: r.club, code: r.code, rank: r.rank, date: todayStr(),
  };
  return String(text).replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => (map[k] ?? "").toString());
}

export const newId = () => Math.random().toString(36).slice(2, 10);

const base = (type, x, y, w, h) => ({ id: newId(), type, x, y, w, h });

export const makeText = (text, o = {}) => ({
  ...base("text", o.x ?? 20, o.y ?? 100, o.w ?? 170, 0),
  text, fontSize: o.fontSize ?? 16, font: o.font ?? "helvetica",
  bold: !!o.bold, italic: !!o.italic, color: o.color ?? "#111111", align: o.align ?? "center",
});
export const makeImage = (src, o = {}) => ({
  ...base("image", o.x ?? 75, o.y ?? 15, o.w ?? 60, o.h ?? 35), src: src || "",
  label: o.label || "Image",
});
export const makeLine = (o = {}) => ({
  ...base("line", o.x ?? 30, o.y ?? 250, o.w ?? 60, 0),
  color: o.color ?? "#111111", strokeWidth: o.strokeWidth ?? 0.4,
});
export const makeRect = (o = {}) => ({
  ...base("rect", o.x ?? 10, o.y ?? 10, o.w ?? 190, o.h ?? 277),
  color: o.color ?? "#111111", strokeWidth: o.strokeWidth ?? 1, fill: o.fill || "",
});

const classic = () => [
  makeImage("club_logo", { x: 70, y: 14, w: 70, h: 36, label: "Club logo" }),
  makeText("Title", { y: 60, fontSize: 32, font: "times", bold: true }),
  makeText("{{competition}}", { y: 95, fontSize: 22 }),
  makeText("{{season}}", { y: 115, fontSize: 18 }),
  makeText("Image Title:  {{image_title}}", { y: 150, fontSize: 16, bold: true }),
  makeText("Member:  {{member}}", { y: 175, fontSize: 16, bold: true }),
  makeText("Judge:  {{judge}}", { y: 200, fontSize: 16, bold: true }),
  makeText("Chairman:", { x: 40, y: 252, w: 40, fontSize: 14, font: "times", align: "left" }),
  makeImage("club_signature", { x: 85, y: 236, w: 70, h: 26, label: "Signature" }),
];

const modern = () => [
  makeRect({ x: 10, y: 10, w: 190, h: 277, color: "#1d4ed8", strokeWidth: 2 }),
  makeRect({ x: 14, y: 14, w: 182, h: 269, color: "#1d4ed8", strokeWidth: 0.4 }),
  makeImage("club_logo", { x: 80, y: 22, w: 50, h: 28, label: "Club logo" }),
  makeText("{{club}}", { y: 54, fontSize: 13, bold: true, color: "#333333" }),
  makeText("TITLE", { y: 72, fontSize: 38, bold: true, color: "#1d4ed8" }),
  makeText("{{competition}}", { y: 92, fontSize: 14, color: "#555555" }),
  makeText("Awarded to", { y: 120, fontSize: 12, color: "#555555" }),
  makeText("{{member}}", { y: 129, fontSize: 28, bold: true }),
  makeText("for the image", { y: 152, fontSize: 12, color: "#555555" }),
  makeText("“{{image_title}}”", { y: 160, fontSize: 20, italic: true }),
  makeText("{{place}}  ·  {{season}}", { y: 182, fontSize: 14, bold: true, color: "#1d4ed8" }),
  makeText("Score {{score}}   ·   Judge: {{judge}}", { y: 196, fontSize: 11, color: "#555555" }),
  makeImage("club_signature", { x: 32, y: 226, w: 56, h: 22, label: "Signature" }),
  makeLine({ x: 30, y: 250, w: 60, color: "#333333", strokeWidth: 0.4 }),
  makeText("Chairman", { x: 30, y: 252, w: 60, fontSize: 10, color: "#555555" }),
  makeLine({ x: 120, y: 250, w: 60, color: "#333333", strokeWidth: 0.4 }),
  makeText("{{season}}", { x: 120, y: 238, w: 60, fontSize: 13 }),
  makeText("Season", { x: 120, y: 252, w: 60, fontSize: 10, color: "#555555" }),
];

const elegant = () => [
  makeLine({ x: 30, y: 30, w: 150, color: "#b8860b", strokeWidth: 0.6 }),
  makeLine({ x: 30, y: 267, w: 150, color: "#b8860b", strokeWidth: 0.6 }),
  makeImage("club_logo", { x: 85, y: 38, w: 40, h: 24, label: "Club logo" }),
  makeText("{{club}}", { y: 66, fontSize: 12, font: "times", color: "#666666" }),
  makeText("Title", { y: 80, fontSize: 36, font: "times", italic: true, color: "#b8860b" }),
  makeText("{{competition}}", { y: 106, fontSize: 15, font: "times", color: "#444444" }),
  makeText("is presented to", { y: 120, fontSize: 12, font: "times", italic: true, color: "#555555" }),
  makeText("{{member}}", { y: 128, fontSize: 28, font: "times", bold: true }),
  makeText("for “{{image_title}}”", { y: 152, fontSize: 17, font: "times" }),
  makeText("{{season}}", { y: 168, fontSize: 13, font: "times", color: "#444444" }),
  makeText("{{place}}   ·   Judged by {{judge}}", { y: 180, fontSize: 12, font: "times", color: "#666666" }),
  makeImage("club_signature", { x: 30, y: 220, w: 60, h: 22, label: "Signature" }),
  makeLine({ x: 30, y: 244, w: 60, color: "#b8860b", strokeWidth: 0.4 }),
  makeText("Chairman", { x: 30, y: 246, w: 60, fontSize: 11, font: "times", color: "#666666" }),
];

const minimal = () => [
  makeText("{{club}}", { x: 20, y: 24, w: 130, fontSize: 10, color: "#666666", align: "left" }),
  makeImage("club_logo", { x: 158, y: 16, w: 32, h: 20, label: "Club logo" }),
  makeText("Title", { x: 20, y: 66, w: 170, fontSize: 18, color: "#666666", align: "left" }),
  makeText("{{competition}}", { x: 20, y: 76, w: 170, fontSize: 12, color: "#666666", align: "left" }),
  makeText("{{member}}", { x: 20, y: 96, w: 170, fontSize: 40, bold: true, align: "left" }),
  makeText("{{place}}", { x: 20, y: 126, w: 170, fontSize: 18, color: "#d97706", align: "left", bold: true }),
  makeText("{{image_title}}", { x: 20, y: 142, w: 170, fontSize: 16, align: "left" }),
  makeText("Score {{score}}  ·  Judged by {{judge}}  ·  {{season}}", { x: 20, y: 158, w: 170, fontSize: 10, color: "#666666", align: "left" }),
  makeImage("club_signature", { x: 20, y: 232, w: 60, h: 22, label: "Signature" }),
  makeLine({ x: 20, y: 255, w: 70, color: "#111111", strokeWidth: 0.3 }),
  makeText("Chairman", { x: 20, y: 257, w: 70, fontSize: 9, color: "#666666", align: "left" }),
];

export const STOCK_DESIGNS = [
  { id: "stock-classic", name: "Classic", background: "#ffffff", stock: true, build: classic },
  { id: "stock-modern", name: "Modern bordered", background: "#ffffff", stock: true, build: modern },
  { id: "stock-elegant", name: "Elegant", background: "#fffdf7", stock: true, build: elegant },
  { id: "stock-minimal", name: "Minimal", background: "#ffffff", stock: true, build: minimal },
];

export const CATEGORIES = [
  { key: "top_scorers", label: "Top scorers" },
  { key: "first_places", label: "1st places" },
  { key: "aggregate", label: "Aggregate" },
  { key: "general", label: "Other" },
];
export const categoryLabel = (k) => (CATEGORIES.find((c) => c.key === k) || CATEGORIES[3]).label;

// Aggregate certificates list every winning image (one per line) and show the total.
const forCategory = (els, category) =>
  category !== "aggregate" ? els : els.map((e) => {
    if (e.type !== "text") return e;
    if (/\{\{\s*image_title\s*\}\}/.test(e.text)) {
      return { ...e, fontSize: Math.max(9, (e.fontSize || 16) - 3), text: e.text.replace(/Image Title:\s*/i, "Images:  ").replace(/for\s+/i, "") };
    }
    if (/^\{\{\s*season\s*\}\}$/.test(e.text.trim())) return { ...e, text: "Total {{score}}  ·  {{season}}" };
    return e;
  });

export function stockDesignFor(stock, category) {
  return {
    based_on: stock.id, category, name: `${categoryLabel(category)} · ${stock.name}`,
    background: stock.background, elements: forCategory(stock.build(), category),
  };
}

// First-visit starter set: every stock look × every certificate type (4 per type).
export const seedDesigns = () =>
  CATEGORIES.filter((c) => c.key !== "general").flatMap((c) => STOCK_DESIGNS.map((s) => stockDesignFor(s, c.key)));

export function instantiateStock(stock) {
  return { id: null, based_on: stock.id, name: stock.name, background: stock.background, elements: stock.build() };
}

export const blankDesign = () => ({ id: null, based_on: null, name: "Blank", background: "#ffffff", elements: [] });
